import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Divider, makeStyles, Theme, Typography } from '@material-ui/core';
import { DateRange, MobileDateRangePicker } from '@material-ui/pickers';
import moment from 'moment';
import { AutocompleteChangeReason } from '@material-ui/lab/Autocomplete/Autocomplete';

import { Page } from '../../components/Page';
import { BackAppBar } from '../../components/BackAppBar';
import { PageBody } from '../../components/PageBody';
import { FormGroup } from '../../components/FormGroup';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';
import { Block } from '../../components/Block';
import { RepairProgressRequestData } from '../../api/repairProgress';
import { api } from '../../api';
import { StatusFalseError } from '../../api/error/StatusFalseError';
import { useLoading } from '../../units/loading';
import { useAlert } from '../../units/alert';
import { Repair } from '../../data/Repair';
import { usePartner } from '../../hooks/usePartner';
import { Autocomplete } from '../../components/Autocomplete';
import { useProductList } from '../../hooks/useProductList';
import { useRepairStageList } from '../../hooks/useRepairStage';
import { Select } from '../../components/Select';
import { RepairResultCardList } from './components/RepairResultCardList';
import { useProductModelOptions } from '../../hooks/useProductModelOptions';

type SearchPageProps = {};

SearchPage.defaultProps = {};

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        '& .MuiPickersDateRangePickerInput-root': {
            margin: theme.spacing(1),
        },
    },
}));

function useFormData() {
    const [formData, setFormData] = useState<RepairProgressRequestData>({
        type: '1',
        barcode: '',
        sn_code: '',
        product_model_id: '',
        repair_date: '',
        repair_date_e: '',
        stage_name_id: '',
        line_uid: '',
        login: '',
        name: '',
        email: '',
    });

    return {
        formData,
        setFormData,
    };
}

function SearchPage(props: SearchPageProps): React.ReactElement {
    const classes = useStyles();
    const history = useHistory();
    const { setIsLoading } = useLoading();
    const alert = useAlert();
    const { formData, setFormData } = useFormData();
    const [selectedDate, setSelectedDate] = useState<DateRange<moment.Moment>>([null, null]);
    const { partner, isLoading: isPartnerLoading, partnerLineUid } = usePartner();
    const { productList } = useProductList({
        line_uid: partnerLineUid || '',
        login: partner?.email || '',
    });
    const productModelOptions = useProductModelOptions(productList);
    const { repairStageList } = useRepairStageList();
    const [repairList, setRepairList] = useState<Repair[]>([]);
    const [searchMail, setSearchMail] = useState('');

    useEffect(() => {
        setIsLoading(isPartnerLoading);
        if (partner) {
            handleSubmitClick();
        }
    }, [isPartnerLoading]);

    function handleDateChange(date: DateRange<moment.Moment>) {
        setSelectedDate(date);
    }

    function handleBarcodeChange(value: string, reason?: AutocompleteChangeReason | 'change') {
        setFormData((prevState) => ({
            ...prevState,
            barcode: value,
        }));
    }

    function handleSubmitClick() {
        setIsLoading(true);
        setRepairList([]);
        setSearchMail('');
        api.repairProgress({
            ...formData,
            login: partner?.email || '',
            line_uid: partnerLineUid || '',
            type: partner ? '1' : '2',
            repair_date: selectedDate[0] ? selectedDate[0].format('YYYY-MM-DD') : '',
            repair_date_e: selectedDate[1] ? selectedDate[1].format('YYYY-MM-DD') : '',
        })
            .then((resData) => {
                setIsLoading(false);
                setRepairList(resData);
                if (!partner) {
                    setSearchMail(formData.email);
                }
            })
            .catch(async (e) => {
                setIsLoading(false);
                if (e instanceof StatusFalseError) {
                    const errorData = await e.response.json();
                    alert.show({
                        type: 'error',
                        title: '錯誤',
                        message: errorData.msg,
                    });
                }
            });
    }

    function renderMemberForm() {
        return (
            <FormGroup>
                <Autocomplete
                    options={productList.map((item) => ({ text: `${item.barcode} - ${item.product_model_id} - ${item.sn_code}`, value: item.barcode }))}
                    label="產品辨識碼"
                    freeSolo
                    onChange={handleBarcodeChange}
                    value={formData.barcode}
                />
                <Autocomplete
                    options={productModelOptions}
                    label="產品型號"
                    freeSolo
                    onChange={(value) => {
                        setFormData({
                            ...formData,
                            product_model_id: value,
                        });
                    }}
                />
                <MobileDateRangePicker
                    value={selectedDate}
                    onChange={handleDateChange}
                    inputFormat="YYYY-MM-DD"
                    clearable
                    startText="開始日期"
                    endText="結束日期"
                    showToolbar={false}
                    renderInput={(startProps, endProps) => {
                        function renderInputValue() {
                            if (!startProps?.inputProps?.value || !endProps?.inputProps?.value) {
                                return '';
                            }

                            return `${startProps?.inputProps?.value || ''} ~ ${endProps?.inputProps?.value || ''}`;
                        }

                        return (
                            <TextField
                                {...startProps}
                                label="報修日期區間"
                                inputProps={{
                                    ...startProps.inputProps,
                                    value: renderInputValue(),
                                }}
                                helperText=""
                                fullWidth
                                style={{ margin: 0 }}
                            />
                        );
                    }}
                />
                <Select
                    labelId="input-repair-stage"
                    label="維修狀態"
                    options={repairStageList.map((stage) => ({
                        text: stage.front_stage,
                        value: stage.front_stage,
                    }))}
                    value={formData.stage_name_id}
                    onChange={(e) => setFormData({
                        ...formData,
                        stage_name_id: e.target.value as string,
                    })}
                />
                <Button onClick={handleSubmitClick}>查詢資料</Button>
            </FormGroup>
        );
    }

    function renderGuestForm() {
        return (
            <FormGroup>
                <TextField
                    label="報修單號"
                    value={formData.name}
                    onChange={(e) => setFormData({
                        ...formData,
                        name: e.target.value,
                    })}
                />
                <TextField
                    label="E-mail"
                    value={formData.email}
                    onChange={(e) => setFormData({
                        ...formData,
                        email: e.target.value,
                    })}
                />
                <Button onClick={handleSubmitClick}>查詢資料</Button>
                <Typography variant="body2" color="error">*目前尚未登入帳號，建議登入帳號可查詢更多資料</Typography>
            </FormGroup>
        );
    }

    function handleRepairCardClick(r: Repair) {
        if (partner) {
            history.push(`/repair/search/${r.name}`);
        } else {
            history.push(`/repair/search/${r.name}?mail=${searchMail}`);
        }
    }

    return (
        <Page className={classes.root}>
            <BackAppBar title="報修查詢" />
            <PageBody>
                {partner ? renderMemberForm() : renderGuestForm()}
                <Divider />
                <Block p={1} row justifyContent="space-between" alignItems="center">
                    <Typography variant="h5">查詢結果</Typography>
                    <Typography variant="subtitle1" color="textSecondary">*顯示已出貨一個月內的資料</Typography>
                </Block>
                <RepairResultCardList
                    repairList={repairList}
                    onClick={handleRepairCardClick}
                />
            </PageBody>
        </Page>
    );
}

export { SearchPage };
